import { create } from "zustand";
import { UserSessionLog } from "@/types";
import { supabase } from "@/lib/supabase";
import { getCurrentHospitalId } from "@/lib/hospitalContext";

interface SessionLogsState {
  logs: UserSessionLog[];
  loading: boolean;
  error: string | null;
  fetchLogs: () => Promise<void>;
  clearLogs: () => void;
}

function mapLogRow(log: Record<string, unknown>): UserSessionLog {
  return {
    id: log.id as string,
    userId: log.user_id as string,
    hospitalId: log.hospital_id as string | undefined,
    userEmail: log.user_email as string | undefined,
    userName: log.user_name as string,
    userRole: log.user_role as string,
    action: log.action as UserSessionLog["action"],
    details: (log.details as string) || "",
    timestamp: log.timestamp as string,
    ipAddress: log.ip_address as string | undefined,
    deviceInfo: log.device_info as string | undefined,
  };
}

const LOG_COLUMNS =
  "id, user_id, hospital_id, user_name, user_role, user_email, action, details, timestamp, ip_address, device_info";
const SESSION_LOG_RETENTION_DAYS = 30;

export const useSessionLogsStore = create<SessionLogsState>((set) => ({
  logs: [],
  loading: false,
  error: null,

  clearLogs: () => set({ logs: [], loading: false, error: null }),

  fetchLogs: async () => {
    set({ loading: true, error: null });

    const hospitalId = await getCurrentHospitalId();
    const retentionCutoff = new Date(
      Date.now() - SESSION_LOG_RETENTION_DAYS * 24 * 60 * 60 * 1000
    ).toISOString();

    let query = supabase
      .from("user_session_logs")
      .select(LOG_COLUMNS)
      .gte("timestamp", retentionCutoff)
      .order("timestamp", { ascending: false });

    if (hospitalId) {
      query = query.eq("hospital_id", hospitalId);
    }

    const { data, error } = await query;

    if (error) {
      const { data: dataFallback, error: errorFallback } = await supabase
        .from("user_session_logs")
        .select(
          "id, user_id, user_name, user_role, action, details, timestamp, ip_address, device_info"
        )
        .gte("timestamp", retentionCutoff)
        .order("timestamp", { ascending: false });

      if (errorFallback) {
        set({ error: errorFallback.message, loading: false });
        return;
      }

      set({
        logs: (dataFallback || []).map((log) => mapLogRow(log as Record<string, unknown>)),
        loading: false,
        error: null,
      });
      return;
    }

    set({
      logs: (data || []).map((log) => mapLogRow(log as Record<string, unknown>)),
      loading: false,
      error: null,
    });
  },
}));
